import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { Severity } from '@/types/alerts';

/**
 * `<StatusBadge>` — the ONE fleetGUARDIAN status pill. Colour is ALWAYS paired
 * with a word (children) and a dot, per the "color + word, never color alone"
 * accessibility rule. Six semantic tones, each a soft `bg-50 / border-200 /
 * text-700` triplet with a `-500` dot.
 */
export type StatusTone =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'special'
  | 'neutral';

const TONE: Record<StatusTone, { badge: string; dot: string }> = {
  success: {
    badge: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    dot: 'bg-emerald-500',
  },
  warning: {
    badge: 'bg-amber-50 border-amber-200 text-amber-700',
    dot: 'bg-amber-500',
  },
  danger: { badge: 'bg-red-50 border-red-200 text-red-700', dot: 'bg-red-500' },
  info: { badge: 'bg-blue-50 border-blue-200 text-blue-700', dot: 'bg-blue-500' },
  special: {
    badge: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    dot: 'bg-indigo-500',
  },
  neutral: {
    badge: 'bg-gray-100 border-gray-300 text-gray-600',
    dot: 'bg-gray-400',
  },
};

export interface StatusBadgeProps {
  tone: StatusTone;
  children: ReactNode;
  className?: string;
  /** Hide the leading dot (rare — the dot reinforces the tone for a11y). */
  showDot?: boolean;
}

export function StatusBadge({
  tone,
  children,
  className,
  showDot = true,
}: StatusBadgeProps): JSX.Element {
  const t = TONE[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-medium',
        t.badge,
        className,
      )}
    >
      {showDot && <span className={cn('h-1.5 w-1.5 rounded-full', t.dot)} />}
      {children}
    </span>
  );
}

/** Map an alert severity to a StatusBadge tone (Critical/High → danger, …). */
export function severityTone(severity: Severity): StatusTone {
  switch (severity) {
    case 'Critical':
    case 'High':
      return 'danger';
    case 'Medium':
      return 'warning';
    case 'Low':
      return 'info';
    case 'Watch':
      return 'neutral';
    case 'Normal':
      return 'success';
    default:
      return 'neutral';
  }
}

export default StatusBadge;
